
import { useState, useEffect } from "react";
import { DiaryEntry } from "@/utils/generator";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";

interface EntryTranslatorProps {
  entry: DiaryEntry;
  setTranslatedContent: (content: string | null) => void;
}

const EntryTranslator = ({ entry, setTranslatedContent }: EntryTranslatorProps) => {
  const { language } = useLanguage();
  const [isTranslating, setIsTranslating] = useState(false);
  
  // Reset translation and translate again when language or entry changes
  useEffect(() => {
    if (language === 'en') {
      setTranslatedContent(null);
    } else {
      translateContent();
    }
  }, [language, entry.id, entry.entryText]);
  
  const translateContent = async () => {
    // Skip translation if already in English (assumed original language)
    if (language === 'en') {
      setTranslatedContent(null);
      return;
    }
    
    setIsTranslating(true);
    
    try {
      // For demonstration, we're using a simulated translation
      // In a real app, this would be an API call to a translation service
      setTimeout(() => {
        let translated = "";
        
        if (language === 'ja') {
          translated = generateJapaneseTranslation(entry);
        } else if (language === 'es') {
          translated = generateSpanishTranslation(entry);
        } else if (language === 'zh') {
          translated = generateChineseTranslation(entry);
        } else {
          // Default to original text if language not supported
          translated = entry.entryText;
        }
        
        setTranslatedContent(translated);
        setIsTranslating(false);
      }, 500);
    } catch (error) {
      console.error("Translation error:", error);
      setIsTranslating(false);
    }
  };
  
  // Helper functions to generate translations (simulated for demonstration)
  const generateJapaneseTranslation = (entry: DiaryEntry): string => {
    return `親愛なる日記、今日は${entry.babyName}と一緒に過ごしました。\n\n${
      entry.mood === 'happy' 
        ? `${entry.babyName}は今日一日中笑顔で輝いていました。` 
        : entry.mood === 'calm' 
          ? `${entry.babyName}は今日とても穏やかで満足している様子でした。` 
          : entry.mood === 'curious' 
            ? `${entry.babyName}は今日、すべてを発見するミッションに取り組んでいました！` 
            : entry.mood === 'fussy' 
              ? `今日は${entry.babyName}がより多くの慰めと注目を必要としているようで、少し大変でした。` 
              : `${entry.babyName}は今日特に眠そうで、いつもより長い昼寝をしていました。`
    }\n\n${entry.activities.length > 0 ? `今日の活動: ${entry.activities.join('、')}` : ''}\n\n${
      entry.milestones.length > 0 ? `特別な成長の記録: ${entry.milestones.join('、')}` : ''
    }\n\n${entry.babyName}が${entry.age}で成長するのは素晴らしいことです。毎日が新しい発見と深い愛をもたらします。`;
  };
  
  const generateSpanishTranslation = (entry: DiaryEntry): string => {
    return `Querido diario, hoy pasé el día con ${entry.babyName}.\n\n${
      entry.mood === 'happy' 
        ? `${entry.babyName} estuvo radiante y sonriente durante todo el día.` 
        : entry.mood === 'calm' 
          ? `${entry.babyName} estuvo muy tranquilo y contento hoy.` 
          : entry.mood === 'curious' 
            ? `${entry.babyName} estuvo en una misión para descubrirlo todo hoy!` 
            : entry.mood === 'fussy' 
              ? `Hoy fue un poco difícil ya que ${entry.babyName} parecía necesitar más consuelo y atención.` 
              : `${entry.babyName} parecía especialmente somnoliento hoy y tomó siestas más largas de lo habitual.`
    }\n\n${entry.activities.length > 0 ? `Actividades de hoy: ${entry.activities.join(', ')}` : ''}\n\n${
      entry.milestones.length > 0 ? `Hitos especiales: ${entry.milestones.join(', ')}` : ''
    }\n\nEs maravilloso ver a ${entry.babyName} crecer a los ${entry.age}. Cada día trae nuevos descubrimientos y amor profundo.`;
  };
  
  const generateChineseTranslation = (entry: DiaryEntry): string => {
    return `亲爱的日记，今天我和${entry.babyName}一起度过了一天。\n\n${
      entry.mood === 'happy' 
        ? `${entry.babyName}今天整天都在灿烂地笑着。` 
        : entry.mood === 'calm' 
          ? `${entry.babyName}今天非常平静和满足。` 
          : entry.mood === 'curious' 
            ? `${entry.babyName}今天一直在探索发现一切！` 
            : entry.mood === 'fussy' 
              ? `今天有点难，因为${entry.babyName}似乎需要更多的安慰和关注。` 
              : `${entry.babyName}今天看起来特别困倦，比平时睡得更久。`
    }\n\n${entry.activities.length > 0 ? `今天的活动：${entry.activities.join('，')}` : ''}\n\n${
      entry.milestones.length > 0 ? `特别的里程碑：${entry.milestones.join('，')}` : ''
    }\n\n看着${entry.babyName}在${entry.age}成长是多么美妙。每一天都带来新的发现和深深的爱。`;
  };
  
  return (
    <div className="sr-only" aria-hidden="true">
      {isTranslating ? t('common.translating', language) : "Translation component"}
    </div>
  );
};

export default EntryTranslator;
